import React, { useContext } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalHeader, ModalBody, ModalFooter, Button, Text } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';

import UserContext from '../../../contexts/UserContext';
import Input from '../../../components/Input';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { INTERNAL_AMAZON_AGED_PARCEL_SCAN } from './query';

const AgedParcelScanSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar eingeben'),
});

interface AgedParcelScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  trackingNumber: string;
  amazonAccessPointId: string;
}

const AgedParcelScanModal: React.FC<AgedParcelScanModalProps> = ({ isOpen, onClose, refetch, trackingNumber, amazonAccessPointId }) => {
  const [internalAgedParcelScan, { loading }] = useMutation(INTERNAL_AMAZON_AGED_PARCEL_SCAN);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Aged Parcel Scan simulieren</ModalHeader>

        <Formik
          initialValues={{
            comment: '',
          }}
          validationSchema={AgedParcelScanSchema}
          onSubmit={async ({ comment }) => {
            try {
              const result = await internalAgedParcelScan({
                variables: {
                  trackingNumber,
                  amazonAccessPointId,
                  comment,
                  internalUserId: user.id,
                },
              });

              if (result.data?.internalAmazonAgedParcelScan === 'success') {
                toast({
                  title: 'Erfolgreich',
                  description: 'Aged Parcel Scan wurde erfolgreich ausgelöst',
                  status: 'success',
                });
                await refetch();
                onClose();
              } else {
                toast({
                  title: 'Fehler',
                  description: 'Der Scan konnte nicht durchgeführt werden. Überprüfe den Paketstatus.',
                  status: 'error',
                });
              }
            } catch (error: any) {
              toast({
                title: 'Fehler',
                description: error.message || 'Ein unbekannter Fehler ist aufgetreten',
                status: 'error',
              });
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <ModalBody>
                <Text mb="4" fontSize="sm">
                  Du bist dabei, einen Aged Parcel Scan für das Paket <strong>{trackingNumber}</strong> auszulösen. Bitte gib einen
                  Kommentar ein, warum du diese Aktion durchführst.
                </Text>
                <Input name="comment" label="Kommentar" placeholder="Grund für die Simulation..." />
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose} isDisabled={loading}>
                  Abbrechen
                </Button>
                <Button ml="4" size="sm" colorScheme="brand" type="submit" isLoading={loading || isSubmitting}>
                  Scan ausführen
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default AgedParcelScanModal;
